import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Chip,
  Collapse,
  IconButton,
  Divider,
  alpha,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChatIcon from '@mui/icons-material/Chat';
import BoltIcon from '@mui/icons-material/Bolt';
import AutoDeleteOutlinedIcon from '@mui/icons-material/AutoDeleteOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import SubtitlesOutlinedIcon from '@mui/icons-material/SubtitlesOutlined';
import QueueOutlinedIcon from '@mui/icons-material/QueueOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import ViewAgendaOutlinedIcon from '@mui/icons-material/ViewAgendaOutlined';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import {
  getAnnouncementSeen,
  markAnnouncementSeen,
} from '../helper/announcementsApi';

// Announcement-Versionen werden pro Generation getrennt verfolgt, damit das
// Modal nicht alles auf einmal "verbraucht", wenn ein User mehrere Releases
// auf einmal nachholt. Latest-Release löst Auto-Open aus; ältere Sections
// werden eingeklappt angezeigt, wenn sie bereits gesehen wurden — und
// aufgeklappt, falls noch nicht.
const ANNOUNCEMENT_VERSION_LATEST = 'v7_download_speaker_grouping_2026_09';
const ANNOUNCEMENT_VERSION_V2 = 'v5_transcript_editing_2026_04';

// Globales Event, mit dem das Modal von beliebigen Stellen manuell geöffnet
// werden kann (z.B. "Was ist neu?"-Button auf der Startseite):
//   window.dispatchEvent(new CustomEvent('show-whatsnew'))
export const SHOW_WHATS_NEW_EVENT = 'show-whatsnew';

function FeatureRow({ icon, title, children, tone = 'primary' }) {
  const isAccent = tone !== 'primary';
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      sx={
        isAccent
          ? {
              p: 1.5,
              borderRadius: 1.5,
              border: 1,
              borderColor: (theme) => alpha(theme.palette[tone].main, 0.4),
              bgcolor: (theme) => alpha(theme.palette[tone].main, 0.08),
            }
          : undefined
      }
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: '50%',
          bgcolor: `${tone}.main`,
          color: `${tone}.contrastText`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          variant="subtitle2"
          fontWeight={600}
          color={isAccent ? `${tone}.main` : undefined}
        >
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" component="div">
          {children}
        </Typography>
      </Box>
    </Stack>
  );
}

function SectionHeader({ title, subtitle, expanded, onToggle }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      onClick={onToggle}
      sx={{
        cursor: 'pointer',
        userSelect: 'none',
        py: 0.5,
        '&:hover': { color: 'primary.main' },
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, lineHeight: 1.4 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      <IconButton
        size="small"
        sx={{
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 200ms ease',
        }}
        aria-label={expanded ? 'Einklappen' : 'Ausklappen'}
      >
        <ExpandMoreIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}

export default function WhatsNewModal() {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // null = noch nicht geladen; true/false = bekannt
  const [seenLatest, setSeenLatest] = useState(null);
  const [seenV2, setSeenV2] = useState(null);
  // Collapse-State für die 2.0-Section. Initial null = folgt seenV2 (siehe Effect).
  const [v2Expanded, setV2Expanded] = useState(null);

  // Initialer Check: beide Versionen parallel laden.
  useEffect(() => {
    if (!isAuthenticated) return;
    let active = true;
    (async () => {
      try {
        const [resLatest, resV2] = await Promise.all([
          getAnnouncementSeen(instance, ANNOUNCEMENT_VERSION_LATEST).catch(() => ({ seen: false })),
          getAnnouncementSeen(instance, ANNOUNCEMENT_VERSION_V2).catch(() => ({ seen: false })),
        ]);
        if (!active) return;
        const latest = Boolean(resLatest?.seen);
        const v2 = Boolean(resV2?.seen);
        setSeenLatest(latest);
        setSeenV2(v2);
        // Auto-Open, wenn entweder die neuen 2.0.2-Features oder die 2.0-Generation
        // noch nicht gesehen wurden. So bekommt ein neuer User auch die großen
        // 2.0-Themen mit, selbst wenn er erst mit 2.0.2 einsteigt.
        if (!latest || !v2) setOpen(true);
      } catch {
        // Bei Fehler nichts zeigen — lieber kein Modal als ein flackerndes.
      }
    })();
    return () => {
      active = false;
    };
  }, [isAuthenticated, instance]);

  // Manuelles Öffnen über Event: alle "Seen"-Werte einmal frisch holen, damit
  // die Collapse-Logik wieder zur Wahrheit passt.
  useEffect(() => {
    const handler = async () => {
      try {
        const [resLatest, resV2] = await Promise.all([
          getAnnouncementSeen(instance, ANNOUNCEMENT_VERSION_LATEST).catch(() => ({ seen: true })),
          getAnnouncementSeen(instance, ANNOUNCEMENT_VERSION_V2).catch(() => ({ seen: true })),
        ]);
        setSeenLatest(Boolean(resLatest?.seen));
        setSeenV2(Boolean(resV2?.seen));
      } catch {
        // Fallback: einfach öffnen, beide Sections als gesehen behandeln.
      }
      setOpen(true);
    };
    window.addEventListener(SHOW_WHATS_NEW_EVENT, handler);
    return () => window.removeEventListener(SHOW_WHATS_NEW_EVENT, handler);
  }, [instance]);

  // Sobald wir wissen, ob v2 schon gesehen wurde: Collapse-State entsprechend
  // initialisieren. Nicht überschreiben, wenn der User schon manuell getoggelt
  // hat (v2Expanded ist dann nicht null).
  useEffect(() => {
    if (seenV2 === null) return;
    setV2Expanded((current) => (current === null ? !seenV2 : current));
  }, [seenV2]);

  const handleClose = async () => {
    setSaving(true);
    // Beide Versionen als gesehen markieren — der User hat sie ja jetzt im
    // Modal gehabt. Best-effort, Errors stören das Schließen nicht.
    try {
      await Promise.all([
        markAnnouncementSeen(instance, ANNOUNCEMENT_VERSION_LATEST).catch(() => {}),
        markAnnouncementSeen(instance, ANNOUNCEMENT_VERSION_V2).catch(() => {}),
      ]);
    } finally {
      setSaving(false);
      setOpen(false);
    }
  };

  const isV2Expanded = v2Expanded === null ? !seenV2 : v2Expanded;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoAwesomeIcon color="primary" />
        Transcribe 2.0.5
        <Chip
          size="small"
          color="primary"
          label="Neu"
          sx={{ ml: 1 }}
        />
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Typography variant="body2" color="text.secondary">
            Neu: Der Download übernimmt jetzt die Darstellung, die du in der
            Ansicht gewählt hast. Weiterhin frisch dabei: die kompakte
            Sprecher-Ansicht und das manuelle Trennen von Sprechern.
          </Typography>

          <FeatureRow
            icon={<FileDownloadOutlinedIcon fontSize="small" />}
            title="Download pro Sprecher zusammengefasst"
            tone="primary"
          >
            Im Download-Dialog gibt es jetzt die Option
            <em> „Beiträge pro Sprecher zusammenfassen"</em>: Aufeinanderfolgende
            Segmente desselben Sprechers landen als ein zusammenhängender Absatz
            in der Textdatei, statt Zeile für Zeile. Vorbelegt ist, was du gerade
            in der Ansicht siehst — kompakt oder Standard — und du kannst es für
            einen einzelnen Download abweichend setzen. Für Untertitel (.srt)
            bleibt es bewusst bei kurzen Cues.
          </FeatureRow>

          <FeatureRow
            icon={<ViewAgendaOutlinedIcon fontSize="small" />}
            title="Kompakte Ansicht pro Sprecher"
            tone="primary"
          >
            Über den Toggle in der Sprecher-Leiste (Listen-Icon ↔ Block-Icon)
            kannst du wählen, ob jedes Segment in einer eigenen Karte erscheint
            (Standard) oder aufeinanderfolgende Segmente desselben Sprechers zu
            einer zusammenhängenden Karte zusammengefasst werden (Kompakt).
            Spart bei langen Aufnahmen deutlich Scroll-Strecke. Die Auswahl
            bleibt geräteübergreifend pro Nutzer gespeichert.
          </FeatureRow>

          <FeatureRow
            icon={<ContentCutOutlinedIcon fontSize="small" />}
            title="Sprecher an jeder Stelle manuell trennen"
            tone="primary"
          >
            Klick auf ein Wort öffnet ein kleines Menü mit zwei Aktionen:
            <em> „Audio ab hier abspielen"</em> oder
            <em> „Sprecher hier trennen"</em>. Wenn die KI zwei Sprecher
            fälschlicherweise zusammengeworfen hat, kannst du jetzt punktgenau
            korrigieren — bestehenden Sprecher zuweisen oder einen neuen mit
            beliebigem Namen anlegen.
          </FeatureRow>

          <Divider />

          <SectionHeader
            title="Highlights aus Transcribe 2.0"
            subtitle={
              seenV2
                ? 'Hast du schon gesehen — klicken zum Ansehen.'
                : 'Noch nicht angesehen — klicken zum Einklappen.'
            }
            expanded={isV2Expanded}
            onToggle={() => setV2Expanded((v) => (v === null ? false : !v))}
          />

          <Collapse in={isV2Expanded} timeout="auto" unmountOnExit>
            <Stack spacing={2.5}>
              <Typography variant="body2" color="text.secondary">
                Transcribe 2.0 bringt eine neue Warteschlange mit Live-Fortschritt —
                du kannst mehrere Aufnahmen gleichzeitig transkribieren lassen und
                musst den Browser nicht mehr offen halten.
              </Typography>

              <FeatureRow
                icon={<QueueOutlinedIcon fontSize="small" />}
                title="Mehrere Aufnahmen gleichzeitig"
              >
                Lade mehrere Dateien auf einmal hoch — alle landen in einer
                Warteschlange und werden nacheinander abgearbeitet. Du siehst
                unten rechts jederzeit, welche Jobs noch laufen.
              </FeatureRow>

              <FeatureRow
                icon={<TimelineOutlinedIcon fontSize="small" />}
                title="Live-Fortschritt pro Stufe"
              >
                Statt „wird verarbeitet" siehst du jetzt genau, was gerade passiert:
                Audio laden, Whisper, Alignment, Sprechererkennung und
                Nachbearbeitung — inklusive Prozentanzeige.
              </FeatureRow>

              <FeatureRow
                icon={<SpeedOutlinedIcon fontSize="small" />}
                title="Schneller transkribieren, weniger klicken"
              >
                Ein optimiertes Modell macht Transkriptionen deutlich schneller.
                Das Upload-Formular ist aufgeräumt: Datei auswählen, Sprache wählen,
                fertig. Sprechererkennung ist automatisch aktiv.
              </FeatureRow>

              <FeatureRow
                icon={<RecordVoiceOverOutlinedIcon fontSize="small" />}
                title="Sprecher-Assistent"
              >
                Nach der Transkription öffnet sich ein Assistent, der dir zu jedem
                erkannten Sprecher einen kurzen Audioausschnitt vorspielt. Du vergibst
                einfach Namen — gleiche Namen führen Sprecher automatisch zusammen.
                Jederzeit über den Button „Sprecher benennen" wieder aufrufbar.
              </FeatureRow>

              <FeatureRow
                icon={<EditNoteOutlinedIcon fontSize="small" />}
                title="Transkript bearbeiten und gezielt anhören"
              >
                Segmente lassen sich jetzt über das Stift-Symbol direkt bearbeiten
                und speichern automatisch beim Verlassen des Textfelds. Wort-Klicks
                springen weiter zur passenden Audiostelle; die Zeitmarke springt zum
                Segmentstart. Oben kannst du außerdem nach einzelnen Sprechern filtern.
              </FeatureRow>

              <FeatureRow icon={<ChatIcon fontSize="small" />} title="Mit dem Transkript chatten">
                Stelle Fragen zu deiner Aufnahme. Die Antwort stützt sich ausschließlich
                auf die relevanten Stellen im Transkript — mit Zeitmarken als Beleg.
              </FeatureRow>

              <FeatureRow icon={<BoltIcon fontSize="small" />} title="1-Click KI-Funktionen">
                Ein Klick genügt: automatisch generierte Meeting-Protokolle,
                Zusammenfassungen, Aufgabenlisten oder eigene Vorlagen. Eigene
                Funktionen legst du unter <strong>1-Click KI-Funktionen</strong>
                im Menü an.
              </FeatureRow>

              <FeatureRow
                icon={<SubtitlesOutlinedIcon fontSize="small" />}
                title="Download als Untertitel (SRT)"
              >
                Beim Herunterladen kannst du jetzt zwischen <strong>Textdatei (.txt)</strong>
                {' '}und <strong>Untertitel (.srt)</strong> wählen — ideal für
                Videoschnitt, YouTube oder Premiere. Sprechernamen lassen sich
                optional mit einschließen.
              </FeatureRow>

              <FeatureRow
                icon={<SyncAltOutlinedIcon fontSize="small" />}
                title="Unter der Haube: stabilere Verbindung"
              >
                Statusupdates kommen jetzt per Push-Verbindung statt per ständigem
                Abfragen. Das spart Netzwerktraffic und übersteht kurze Aussetzer
                automatisch — du musst dafür nichts tun.
              </FeatureRow>

              <FeatureRow
                icon={<AutoDeleteOutlinedIcon fontSize="small" />}
                title="Wichtig: Automatische Bereinigung nach 365 Tagen"
                tone="warning"
              >
                Transkripte und die zugehörigen Audiodateien werden nach 365 Tagen
                automatisch gelöscht. Lade dir wichtige Ergebnisse also rechtzeitig
                herunter, wenn du sie länger aufbewahren möchtest.
              </FeatureRow>
            </Stack>
          </Collapse>

          <Box
            sx={{
              bgcolor: 'action.hover',
              borderRadius: 1,
              p: 1.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Hinweis: Alle KI-Funktionen laufen <strong>lokal</strong> auf unserem
              Server — deine Transkripte verlassen die Infrastruktur nicht.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained" disabled={saving}>
          Verstanden
        </Button>
      </DialogActions>
    </Dialog>
  );
}
